import { useQuery } from '@tanstack/react-query';
import { Badge } from '@monksflow/monks-ui';
import { LockIcon } from 'lucide-react';
import { platformPlanRulesQueryOptions } from '@/entities/media-guideline';
import { severityLabel, tierOfSeverity, TIER_VARIANT } from '../../lib/enforcement';
import { useTranslation } from 'react-i18next';

export interface PlatformRulesPanelProps {
  /** The platform being viewed, or null for the cross-platform tab. */
  platform: string | null;
}

/**
 * What pre-flight enforces before a single house standard is written.
 *
 * Read-only, and that is the point rather than a limitation. These are not choices
 * this workspace made; they are what the platform rejects, most of them written after
 * a real publish came back with the error code quoted in the rule. Switching one off
 * would not change the platform's mind — it would move the rejection from here to
 * publish, where the objects it would have stopped are already live on the ad account.
 *
 * Each page shows only what belongs to it: the universal rules on "All platforms", and
 * on a platform page the rules that are its own. They also repeat under every platform
 * in the response, and listing them again on each page drowned the handful of real
 * differences — every page looked identical because most of it was.
 *
 * A platform with nothing of its own still says so. Silence there reads as "no standard
 * applies here", when in fact every universal check does.
 */
export function PlatformRulesPanel({ platform }: PlatformRulesPanelProps) {
  const { t } = useTranslation('knowledge');
  const { data: platforms, isError, isLoading } = useQuery(platformPlanRulesQueryOptions());

  // Split from the empty case rather than folded into it, which is the failure the note
  // above warns about: a request that did not arrive rendered as "nothing to show", and
  // silence here reads as "no standard applies", when in fact every universal check does.
  if (isError) {
    return (
      <p role="alert" className="text-sm text-muted-foreground">
        {t('mediaGuidelines.platformRulesPanel.couldNotLoadPlatformRules')}
      </p>
    );
  }

  if (isLoading || !platforms?.length) return null;

  // The universal rules are identical under every platform, so any one group answers
  // for them. A platform the response says nothing about has no rules of its own —
  // falling back to another group's would list one platform's rules under another.
  const group = platform ? platforms.find((each) => each.platform === platform) : undefined;
  const rules = platform
    ? (group?.rules.filter((rule) => rule.platformSpecific) ?? [])
    : platforms[0].rules.filter((rule) => !rule.platformSpecific);
  if (!platform && rules.length === 0) return null;

  const blocking = rules.filter((rule) => rule.severity === 'error').length;

  return (
    <section className="flex flex-col gap-2" data-testid="platform-rules-panel">
      <header className="flex flex-wrap items-center gap-2">
        <LockIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="text-sm font-medium">
          {t('mediaGuidelines.platformRulesPanel.monksFlowStandard')}
        </span>
        <span className="text-xs text-muted-foreground">
          {t('mediaGuidelines.platformRulesPanel.alwaysOn')}{' '}
          {rules.length > 0
            ? t('mediaGuidelines.platformRulesPanel.checkCount', {
                count: rules.length,
                blocking,
              })
            : t('mediaGuidelines.platformRulesPanel.nothingSpecificToThisPlatform')}
          {platform !== null && (
            <> {t('mediaGuidelines.platformRulesPanel.allPlatformsChecksRunHereToo')}</>
          )}
        </span>
      </header>

      {rules.length > 0 && (
        <ul className="flex flex-col gap-1">
          {rules.map((rule) => (
            <li
              key={rule.id}
              className="flex items-start justify-between gap-3 rounded-lg border border-border-light bg-card px-3 py-2"
            >
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-sm">{rule.describe}</span>
                <span className="text-xs text-muted-foreground">
                  {/* The id is what every finding this rule produces is stamped with, so
                      quoting one back off a plan is how a reviewer finds it here. */}
                  {rule.id} · {rule.level}
                </span>
              </span>
              <Badge
                variant={TIER_VARIANT[tierOfSeverity(rule.severity)]}
                roundness="full"
                className="shrink-0 font-normal"
              >
                {severityLabel(rule.severity)}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
